import { createContext, use, useEffect, useMemo } from 'react'

import { getErrorContext, useErrorContextActions } from './store'

import type { ErrorContextState } from './types'
import type { PropsWithChildren } from 'react'

const ErrorContext = createContext<ErrorContextState>({ feature: 'unknown' })

type ErrorContextProviderProps = PropsWithChildren<Partial<ErrorContextState>>

/**
 * Provides error context to child components and syncs to Zustand store.
 * Nesting merges with parent context (extends, not replaces).
 *
 * @param feature - Feature area (e.g. 'playbooks', 'alerts')
 * @param section - Sub-section within the feature
 * @param entityId - Entity ID relevant to the context
 * @param metadata - Arbitrary metadata
 */
export function ErrorContextProvider({
	children,
	feature,
	section,
	entityId,
	metadata,
}: ErrorContextProviderProps) {
	const parent = use(ErrorContext)
	const { setContext } = useErrorContextActions()

	const value = useMemo<ErrorContextState>(
		() => ({
			feature: feature ?? parent.feature,
			section: section ?? parent.section,
			entityId: entityId ?? parent.entityId,
			metadata:
				metadata || parent.metadata
					? { ...parent.metadata, ...metadata }
					: undefined,
		}),
		[feature, section, entityId, metadata, parent],
	)

	useEffect(() => {
		// Snapshot store state before overwrite so nested providers can restore
		// the parent's context on unmount instead of resetting to defaults.
		const prev = getErrorContext()
		setContext(value)
		return () => setContext(prev)
	}, [value, setContext])

	return <ErrorContext value={value}>{children}</ErrorContext>
}
